import {
  prop,
  converge,
  always,
  compose,
  pickBy,
  cond,
  T,
  ifElse,
} from 'ramda';
import { stringify } from 'query-string';
import { createSelector } from 'reselect';

import {
  CLIENT_TYPE_QUERY,
  FAILURE_URL_QUERY,
  INTERNAL_FOUR_O_FOUR,
  RETURN_URL_QUERY,
  SUCCESS_PATH,
  OFFER_SUCCESS_PATH,
  SELECTED_SKU,
  SKU_QUERY,
  FREE_TRIAL_PATH,
  REDIRECT_SECURE_PATH,
  SUBSCRIPTIONS_PURCHASE,
  COUPON_QUERY,
  MIGRATION_SUCCESS_PATH,
} from 'app/router/constants/paths';
import { userLocale, isDirectToPay } from 'app/features/user/selectors';
import { domain } from 'config/selectors';
import { getAppName, getOfferId } from 'app/router/selectors';
import { deviceType } from 'config/baseconfig';
import localizeLink from 'app/features/localization/localizeLink';
import {
  selectedBillingPlanHashes,
  propsProductPlanHashes,
  planSelector,
} from 'app/features/plans/selectors';
import { isSkipFreeTrial } from 'app/features/products/selectors';
import { fromProps } from 'app/selectors/props';
import { selectedCoupon } from 'app/features/couponCampaign/selectors';
import { isMigrationCouponFlow } from 'app/features/migration/landing/selectors';

const checkout = prop('checkout');

const clientReturnUrl = compose(prop('clientReturnUrl'), checkout);

const pickQueryParams = (clientReturnUrl, sku) =>
  pickBy(Boolean, {
    [SELECTED_SKU]: sku,
    [RETURN_URL_QUERY]: clientReturnUrl,
  });

const getSkuFromSelectedPlanOrFirstPlan = compose(prop('sku'), planSelector);

const getQueryParamsForDirectToPayUser = converge(pickQueryParams, [
  clientReturnUrl,
  getSkuFromSelectedPlanOrFirstPlan,
]);

const getQueryParamsForFreeTrialUser = converge(pickQueryParams, [
  clientReturnUrl,
  fromProps('sku'),
]);

const getQueryUrlParams = compose(
  encodeURIComponent,
  stringify,
  cond([
    [isDirectToPay, getQueryParamsForDirectToPayUser],
    [isSkipFreeTrial, getQueryParamsForDirectToPayUser],
    [T, getQueryParamsForFreeTrialUser],
  ]),
);

const createQueryUrl = path =>
  createSelector(
    [getAppName, userLocale, getQueryUrlParams],
    (appName, locale, queryUrlParams) =>
      `https://${domain}${localizeLink(
        `/${appName}${path}`,
        locale,
      )}?${queryUrlParams}`,
  );

const getFailureUrl = createQueryUrl(INTERNAL_FOUR_O_FOUR);

const couponSuccessQueryUrl = ifElse(
  isMigrationCouponFlow,
  createQueryUrl(MIGRATION_SUCCESS_PATH),
  createQueryUrl(SUCCESS_PATH),
);

const getSuccessUrl = cond([
  [selectedCoupon, couponSuccessQueryUrl],
  [getOfferId, createQueryUrl(OFFER_SUCCESS_PATH)],
  [T, createQueryUrl(SUCCESS_PATH)],
]);

const getQueryObject = createSelector(
  [getSuccessUrl, getFailureUrl, fromProps('sku'), selectedCoupon],
  (successUrl, failureUrl, sku, coupon) => ({
    [CLIENT_TYPE_QUERY]: `${deviceType}`,
    [RETURN_URL_QUERY]: successUrl,
    [FAILURE_URL_QUERY]: failureUrl,
    ...(sku && { [SKU_QUERY]: sku }),
    ...(coupon && { [COUPON_QUERY]: coupon }),
  }),
);

const getQuery = compose(stringify, getQueryObject);

const buildCheckoutUrl = (path, query) => `https://${domain}${path}?${query}`;

const freeTrialPath = always(`${FREE_TRIAL_PATH}${REDIRECT_SECURE_PATH}`);

const freeTrialUrl = converge(buildCheckoutUrl, [freeTrialPath, getQuery]);

const checkoutPath = ({ initial, recurring } = {}) =>
  `${SUBSCRIPTIONS_PURCHASE}/${initial}/${recurring}`;

const checkoutUrlBySelectedBillingPlan = converge(buildCheckoutUrl, [
  compose(checkoutPath, selectedBillingPlanHashes),
  getQuery,
]);

const checkoutUrlByPropsProduct = converge(buildCheckoutUrl, [
  compose(checkoutPath, propsProductPlanHashes),
  getQuery,
]);

export {
  freeTrialUrl,
  checkoutPath,
  clientReturnUrl,
  checkoutUrlBySelectedBillingPlan,
  checkoutUrlByPropsProduct,
  getSuccessUrl,
  buildCheckoutUrl,
  getQueryObject,
  createQueryUrl,
  pickQueryParams,
};
